import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useBookingStore } from '@/stores/booking-store';
import { i18n } from '@/plugins/i18n';
import { useToast } from '@/composables/useToast';
import { IDocumentSignedManualForm } from '@/interfaces/profile/profile-response.interface';

export const useBooking = () => {
  const getTotal = computed(() => {
    const { taxes } = storeToRefs(useBookingStore());
    const total = getNetTotal.value;

    const totalTaxes = taxes.value.reduce((acc, tax) => {
      return acc + total * (tax.value / 100);
    }, 0);

    return total + totalTaxes;
  });

  const getNetTotal = computed(() => {
    const { rentalUnitSelected, additionalList } = storeToRefs(useBookingStore());
    if (rentalUnitSelected.value && rentalUnitSelected.value.Prices) {
      const netAmount = rentalUnitSelected.value ? rentalUnitSelected.value.Prices[0].amount : 0;

      const totalAdditionalCost = additionalList.value
        .filter((obj) => obj.isChecked)
        .reduce((sum, obj) => sum + obj.amount, 0);

      const deposit = rentalUnitSelected.value
        ? rentalUnitSelected.value.Prices[0].depositAmount
        : 0;

      const reservation = rentalUnitSelected.value
        ? rentalUnitSelected.value.Prices[0].reservationAmount
        : 0;

      return (
        Number(netAmount) + Number(totalAdditionalCost) + Number(deposit) + Number(reservation)
      );
    }

    return 0;
  });

  const getAdditionalCosts = () => {
    const { additionalList } = storeToRefs(useBookingStore());
    return additionalList.value.filter((obj) => obj.isChecked);
  };

  const handleGetLastBooking = async () => {
    const { lastBooking } = storeToRefs(useBookingStore());
    const { getLastBooking } = useBookingStore();

    try {
      const response = await getLastBooking();
      if (response.id && response.RentalUnit) {
        lastBooking.value = response;
      }
    } catch (error) {
      console.log('not booked', error);
    }
  };

  const handleGetBooking = async (bookingId: string) => {
    const { bookingSelected } = storeToRefs(useBookingStore());
    const { getBooking } = useBookingStore();

    try {
      const response = await getBooking(bookingId);
      if (response.id && response.RentalUnit) {
        bookingSelected.value = response;
      }
    } catch (error) {
      console.log('not booked', error);
    }
  };

  const handleGetBookings = async () => {
    const { bookings } = storeToRefs(useBookingStore());
    const { getBookings } = useBookingStore();

    try {
      const response = await getBookings();
      bookings.value = response.rows;
      return response;
    } catch (error) {
      console.log('not booked', error);
    }
  };

  const handleGetReservationContract = async (bookingId: string) => {
    const { contractData, lastBooking } = storeToRefs(useBookingStore());
    const { getReservationContract } = useBookingStore();

    try {
      if (!lastBooking.value?.id) {
        throw new Error('No booking found');
      }
      const response = await getReservationContract(bookingId);
      if (response?.data) {
        contractData.value = response.data;
      }
    } catch (error) {
      console.log('not booked', error);
    }
  };
  const handleSignReservationContract = async (reservationId: string) => {
    const { showToast } = useToast();
    const { signReservationContract } = useBookingStore();
    try {
      const response = await signReservationContract(reservationId);

      if (response) {
        await showToast(i18n.global.t('profile.document.documentToSign'));
        return response;
      }
    } catch (error) {
      await showToast(i18n.global.t('profile.document.documentToSign'), 'error');
      console.log('not booked', error);
    }
  };
  const handleDownloadContract = async (reservationId: string) => {
    const { showToast } = useToast();
    const { signDownloadContract } = useBookingStore();
    try {
      const response = await signDownloadContract(reservationId);

      if (response?.success) {
        await showToast(i18n.global.t('profile.document.documentToSign'));
        return response;
      } else {
        new Error(response?.error);
      }
    } catch (error) {
      await showToast(i18n.global.t('profile.document.downloadContractSigned'), 'error');
      console.log('not booked', error);
    }
  };
  const handleUploadSignedContract = async (
    reservationId: string,
    form: IDocumentSignedManualForm,
  ) => {
    const { showToast } = useToast();
    const { uploadSignedContract } = useBookingStore();
    try {
      const response = await uploadSignedContract(reservationId, form);

      if (response?.success) {
        await showToast(i18n.global.t('profile.document.documentToSign'));
        return response;
      } else {
        new Error(response?.error);
      }
    } catch (error) {
      await showToast(i18n.global.t('profile.document.downloadContractSigned'), 'error');
      console.log('not booked', error);
    }
  };

  return {
    getTotal,
    getAdditionalCosts,
    handleGetLastBooking,
    handleGetBooking,
    handleGetBookings,
    handleGetReservationContract,
    handleSignReservationContract,
    handleDownloadContract,
    handleUploadSignedContract,
    getNetTotal,
  };
};
